import { emptyApi as api } from './emptyApi';
export const addTagTypes = [
  'trips',
  'auth',
  'favorites',
  'stories',
  'profile',
  'interactions',
  'uploads',
  'profile-uploads',
  'publish',
  'permissions',
  'notifications',
] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getTripsByTripId: build.query<GetTripsByTripIdApiResponse, GetTripsByTripIdApiArg>({
        query: (queryArg) => ({ url: `/trips/${queryArg}` }),
        providesTags: ['trips'],
      }),
      patchTripsByTripId: build.mutation<PatchTripsByTripIdApiResponse, PatchTripsByTripIdApiArg>({
        query: (queryArg) => ({
          url: `/trips/${queryArg.tripId}`,
          method: 'PATCH',
          body: queryArg.partialTripBodyRequest,
        }),
        invalidatesTags: ['trips'],
      }),
      deleteTripsByTripId: build.mutation<
        DeleteTripsByTripIdApiResponse,
        DeleteTripsByTripIdApiArg
      >({
        query: (queryArg) => ({ url: `/trips/${queryArg}`, method: 'DELETE' }),
        invalidatesTags: ['trips', 'auth'],
      }),
      getTrips: build.query<GetTripsApiResponse, GetTripsApiArg>({
        query: (queryArg) => ({ url: `/trips/`, params: { role: queryArg } }),
        providesTags: ['trips'],
      }),
      postTrips: build.mutation<PostTripsApiResponse, PostTripsApiArg>({
        query: (queryArg) => ({ url: `/trips/`, method: 'POST', body: queryArg }),
        invalidatesTags: ['trips'],
      }),
      getTripsByUserByUserId: build.query<
        GetTripsByUserByUserIdApiResponse,
        GetTripsByUserByUserIdApiArg
      >({
        query: (queryArg) => ({ url: `/trips/byUser/${queryArg}` }),
        providesTags: ['trips'],
      }),
      postTripsByTripIdFavoriteAndLinkSecret: build.mutation<
        PostTripsByTripIdFavoriteAndLinkSecretApiResponse,
        PostTripsByTripIdFavoriteAndLinkSecretApiArg
      >({
        query: (queryArg) => ({
          url: `/trips/${queryArg.tripId}/favorite/${queryArg.linkSecret}`,
          method: 'POST',
        }),
        invalidatesTags: ['trips', 'favorites'],
      }),
      getTripsFavorites: build.query<GetTripsFavoritesApiResponse, GetTripsFavoritesApiArg>({
        query: () => ({ url: `/trips/favorites` }),
        providesTags: ['trips', 'favorites'],
      }),
      getStoriesByTripByTripId: build.query<
        GetStoriesByTripByTripIdApiResponse,
        GetStoriesByTripByTripIdApiArg
      >({
        query: (queryArg) => ({ url: `/stories/byTrip/${queryArg}` }),
        providesTags: ['stories'],
      }),
      postStoriesByTripByTripId: build.mutation<
        PostStoriesByTripByTripIdApiResponse,
        PostStoriesByTripByTripIdApiArg
      >({
        query: (queryArg) => ({
          url: `/stories/byTrip/${queryArg.tripId}`,
          method: 'POST',
          body: queryArg.storyBodyRequest,
        }),
        invalidatesTags: ['stories', 'trips'],
      }),
      patchStoriesByTripByTripIdStoryAndStoryId: build.mutation<
        PatchStoriesByTripByTripIdStoryAndStoryIdApiResponse,
        PatchStoriesByTripByTripIdStoryAndStoryIdApiArg
      >({
        query: (queryArg) => ({
          url: `/stories/byTrip/${queryArg.tripId}/story/${queryArg.storyId}`,
          method: 'PATCH',
          body: queryArg.partialStoryBodyRequest,
        }),
        invalidatesTags: ['stories', 'trips'],
      }),
      deleteStoriesByTripByTripIdStoryAndStoryId: build.mutation<
        DeleteStoriesByTripByTripIdStoryAndStoryIdApiResponse,
        DeleteStoriesByTripByTripIdStoryAndStoryIdApiArg
      >({
        query: (queryArg) => ({
          url: `/stories/byTrip/${queryArg.tripId}/story/${queryArg.storyId}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['stories'],
      }),
      getProfiles: build.query<GetProfilesApiResponse, GetProfilesApiArg>({
        query: () => ({ url: `/profiles/` }),
        providesTags: ['profile'],
      }),
      putProfiles: build.mutation<PutProfilesApiResponse, PutProfilesApiArg>({
        query: (queryArg) => ({ url: `/profiles/`, method: 'PUT', body: queryArg }),
        invalidatesTags: ['profile'],
      }),
      getProfilesByUserId: build.query<GetProfilesByUserIdApiResponse, GetProfilesByUserIdApiArg>({
        query: (queryArg) => ({ url: `/profiles/${queryArg}` }),
        providesTags: ['profile'],
      }),
      getInteractionsByTripByTripIdAndLinkSecret: build.query<
        GetInteractionsByTripByTripIdAndLinkSecretApiResponse,
        GetInteractionsByTripByTripIdAndLinkSecretApiArg
      >({
        query: (queryArg) => ({
          url: `/interactions/byTrip/${queryArg.tripId}/${queryArg.linkSecret}`,
        }),
        providesTags: ['interactions'],
      }),
      getInteractionsByUser: build.query<
        GetInteractionsByUserApiResponse,
        GetInteractionsByUserApiArg
      >({
        query: () => ({ url: `/interactions/byUser` }),
        providesTags: ['interactions'],
      }),
      postInteractionsByTripByTripIdAndLinkSecretComment: build.mutation<
        PostInteractionsByTripByTripIdAndLinkSecretCommentApiResponse,
        PostInteractionsByTripByTripIdAndLinkSecretCommentApiArg
      >({
        query: (queryArg) => ({
          url: `/interactions/byTrip/${queryArg.tripId}/${queryArg.linkSecret}/comment`,
          method: 'POST',
          body: queryArg.commentBodyRequest,
        }),
        invalidatesTags: ['interactions'],
      }),
      postInteractionsByTripByTripIdAndLinkSecretLike: build.mutation<
        PostInteractionsByTripByTripIdAndLinkSecretLikeApiResponse,
        PostInteractionsByTripByTripIdAndLinkSecretLikeApiArg
      >({
        query: (queryArg) => ({
          url: `/interactions/byTrip/${queryArg.tripId}/${queryArg.linkSecret}/like`,
          method: 'POST',
          body: queryArg.likeBodyRequest,
        }),
        invalidatesTags: ['interactions'],
      }),
      postInteractionsByTripByTripIdAndLinkSecretViews: build.mutation<
        PostInteractionsByTripByTripIdAndLinkSecretViewsApiResponse,
        PostInteractionsByTripByTripIdAndLinkSecretViewsApiArg
      >({
        query: (queryArg) => ({
          url: `/interactions/byTrip/${queryArg.tripId}/${queryArg.linkSecret}/views`,
          method: 'POST',
        }),
        invalidatesTags: ['interactions'],
      }),
      getInteractionsByTripByTripIdViews: build.query<
        GetInteractionsByTripByTripIdViewsApiResponse,
        GetInteractionsByTripByTripIdViewsApiArg
      >({
        query: (queryArg) => ({ url: `/interactions/byTrip/${queryArg.tripId}/views` }),
        providesTags: ['interactions'],
      }),
      deleteInteractionsByTripByTripIdCommentAndCommentId: build.mutation<
        DeleteInteractionsByTripByTripIdCommentAndCommentIdApiResponse,
        DeleteInteractionsByTripByTripIdCommentAndCommentIdApiArg
      >({
        query: (queryArg) => ({
          url: `/interactions/byTrip/${queryArg.tripId}/comment/${queryArg.commentId}`,
          method: 'DELETE',
        }),
        invalidatesTags: ['interactions'],
      }),
      postUploadsByTripByTripIdGenerateUrls: build.mutation<
        PostUploadsByTripByTripIdGenerateUrlsApiResponse,
        PostUploadsByTripByTripIdGenerateUrlsApiArg
      >({
        query: (queryArg) => ({
          url: `/uploads/byTrip/${queryArg.tripId}/generate-urls`,
          method: 'POST',
          body: queryArg.mediaUploadRequest,
        }),
        invalidatesTags: ['uploads'],
      }),
      postUploadsProfilePicture: build.mutation<
        PostUploadsProfilePictureApiResponse,
        PostUploadsProfilePictureApiArg
      >({
        query: (queryArg) => ({ url: `/uploads/profilePicture`, method: 'POST', body: queryArg }),
        invalidatesTags: ['profile-uploads'],
      }),
      postTripsByTripIdPublish: build.mutation<
        PostTripsByTripIdPublishApiResponse,
        PostTripsByTripIdPublishApiArg
      >({
        query: (queryArg) => ({ url: `/trips/${queryArg}/publish`, method: 'POST' }),
        invalidatesTags: ['publish', 'trips'],
      }),
      postAuth: build.mutation<PostAuthApiResponse, PostAuthApiArg>({
        query: (queryArg) => ({
          url: `/auth/`,
          method: 'POST',
          body: queryArg.tokenBodyRequest,
          params: { remember_me: queryArg.rememberMe },
        }),
        invalidatesTags: ['auth'],
      }),
      deleteAuthAccount: build.mutation<DeleteAuthAccountApiResponse, DeleteAuthAccountApiArg>({
        query: () => ({ url: `/auth/account`, method: 'DELETE' }),
        invalidatesTags: ['auth'],
      }),
      postAuthRefresh: build.mutation<PostAuthRefreshApiResponse, PostAuthRefreshApiArg>({
        query: () => ({ url: `/auth/refresh`, method: 'POST' }),
        invalidatesTags: ['auth'],
      }),
      postAuthLogout: build.mutation<PostAuthLogoutApiResponse, PostAuthLogoutApiArg>({
        query: () => ({ url: `/auth/logout`, method: 'POST' }),
        invalidatesTags: ['auth'],
      }),
      getAuthPermissions: build.query<GetAuthPermissionsApiResponse, GetAuthPermissionsApiArg>({
        query: () => ({ url: `/auth/permissions` }),
        providesTags: ['permissions'],
      }),
      getAuthInspect: build.query<GetAuthInspectApiResponse, GetAuthInspectApiArg>({
        query: () => ({ url: `/auth/inspect` }),
        providesTags: ['auth'],
      }),
      getNotifications: build.query<GetNotificationsApiResponse, GetNotificationsApiArg>({
        query: () => ({ url: `/notifications/` }),
        providesTags: ['notifications'],
      }),
      postNotificationsRead: build.mutation<
        PostNotificationsReadApiResponse,
        PostNotificationsReadApiArg
      >({
        query: (queryArg) => ({ url: `/notifications/read`, method: 'POST', body: queryArg }),
        invalidatesTags: ['notifications'],
      }),
      getTripsByTripIdUrl: build.query<GetTripsByTripIdUrlApiResponse, GetTripsByTripIdUrlApiArg>({
        query: (queryArg) => ({ url: `/trips/${queryArg}/url` }),
      }),
      postTripsByTripIdUrlAndLinkSecret: build.mutation<
        PostTripsByTripIdUrlAndLinkSecretApiResponse,
        PostTripsByTripIdUrlAndLinkSecretApiArg
      >({
        query: (queryArg) => ({
          url: `/trips/${queryArg.tripId}/url/${queryArg.linkSecret}`,
          method: 'POST',
        }),
      }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as backendApi };
export type GetTripsByTripIdApiResponse = /** status 200 Trip */ TripResponse;
export type GetTripsByTripIdApiArg = /** tripId */ string;
export type PatchTripsByTripIdApiResponse = /** status 200 Trip */ TripResponse;
export type PatchTripsByTripIdApiArg = {
  /** tripId */
  tripId: string;
  partialTripBodyRequest: PartialTripBodyRequest;
};
export type DeleteTripsByTripIdApiResponse =
  /** status 200 Trip deleted successfully */ DeleteTripResponse;
export type DeleteTripsByTripIdApiArg = /** tripId */ string;
export type GetTripsApiResponse = /** status 200 Trips */ TripsResponse;
export type GetTripsApiArg = /** role to filter with */ 'admin' | 'viewer';
export type PostTripsApiResponse = /** status 200 Trips */ PostTripResponse;
export type PostTripsApiArg = TripBodyRequest;
export type GetTripsByUserByUserIdApiResponse = /** status 200 Trips */ TripsResponse;
export type GetTripsByUserByUserIdApiArg = /** userId */ string;
export type PostTripsByTripIdFavoriteAndLinkSecretApiResponse =
  /** status 200 Favorite status toggled successfully */ ToggleFavoriteResponse;
export type PostTripsByTripIdFavoriteAndLinkSecretApiArg = {
  /** tripId */
  tripId: string;
  /** linkSecret */
  linkSecret: string;
};
export type GetTripsFavoritesApiResponse = /** status 200 Favorite trips */ FavoritesResponse;
export type GetTripsFavoritesApiArg = void;
export type GetStoriesByTripByTripIdApiResponse = /** status 200 Stories */ StoriesResponse;
export type GetStoriesByTripByTripIdApiArg = /** tripId */ string;
export type PostStoriesByTripByTripIdApiResponse = /** status 200 Stories */ StoryResponse;
export type PostStoriesByTripByTripIdApiArg = {
  /** tripId */
  tripId: string;
  storyBodyRequest: StoryBodyRequest;
};
export type PatchStoriesByTripByTripIdStoryAndStoryIdApiResponse =
  /** status 200 Stories */ StoryResponse;
export type PatchStoriesByTripByTripIdStoryAndStoryIdApiArg = {
  /** tripId */
  tripId: string;
  /** storyId */
  storyId: string;
  partialStoryBodyRequest: PartialStoryBodyRequest;
};
export type DeleteStoriesByTripByTripIdStoryAndStoryIdApiResponse =
  /** status 200 Stories */ DeleteResponse;
export type DeleteStoriesByTripByTripIdStoryAndStoryIdApiArg = {
  /** tripId */
  tripId: string;
  /** storyId */
  storyId: string;
};
export type GetProfilesApiResponse = /** status 200 Profile */ OwnProfileResponse;
export type GetProfilesApiArg = void;
export type PutProfilesApiResponse = /** status 200 Profile */ ProfileResponse;
export type PutProfilesApiArg = ProfileBodyRequest;
export type GetProfilesByUserIdApiResponse = /** status 200 Profile */ ProfileResponse;
export type GetProfilesByUserIdApiArg = /** userId */ string;
export type GetInteractionsByTripByTripIdAndLinkSecretApiResponse =
  /** status 200 interactions */ InteractionsResponse;
export type GetInteractionsByTripByTripIdAndLinkSecretApiArg = {
  /** tripId */
  tripId: string;
  /** linkSecret */
  linkSecret: string;
};
export type GetInteractionsByUserApiResponse =
  /** status 200 interactions */ AllInteractionsResponse;
export type GetInteractionsByUserApiArg = void;
export type PostInteractionsByTripByTripIdAndLinkSecretCommentApiResponse =
  /** status 200 interactions */ any;
export type PostInteractionsByTripByTripIdAndLinkSecretCommentApiArg = {
  /** tripId */
  tripId: string;
  /** linkSecret */
  linkSecret: string;
  commentBodyRequest: CommentBodyRequest;
};
export type PostInteractionsByTripByTripIdAndLinkSecretLikeApiResponse =
  /** status 200 success */ any;
export type PostInteractionsByTripByTripIdAndLinkSecretLikeApiArg = {
  /** tripId */
  tripId: string;
  /** linkSecret */
  linkSecret: string;
  likeBodyRequest: LikeBodyRequest;
};
export type PostInteractionsByTripByTripIdAndLinkSecretViewsApiResponse =
  /** status 201 success */ any;
export type PostInteractionsByTripByTripIdAndLinkSecretViewsApiArg = {
  /** tripId */
  tripId: string;
  /** linkSecret */
  linkSecret: string;
};
export type GetInteractionsByTripByTripIdViewsApiResponse =
  /** status 200 interactions */ ViewsResponse;
export type GetInteractionsByTripByTripIdViewsApiArg = {
  /** tripId */
  tripId: string;
  /** linkSecret */
  linkSecret: string;
};
export type DeleteInteractionsByTripByTripIdCommentAndCommentIdApiResponse =
  /** status 201 interactions */ any;
export type DeleteInteractionsByTripByTripIdCommentAndCommentIdApiArg = {
  /** tripId */
  tripId: string;
  /** commentId */
  commentId: string;
};
export type PostUploadsByTripByTripIdGenerateUrlsApiResponse =
  /** status 200 Trip */ MediaUploadResponse;
export type PostUploadsByTripByTripIdGenerateUrlsApiArg = {
  /** tripId */
  tripId: string;
  mediaUploadRequest: MediaUploadRequest;
};
export type PostUploadsProfilePictureApiResponse = /** status 200 Trip */ MediaUploadResponse;
export type PostUploadsProfilePictureApiArg = ProfilePicUploadRequest;
export type PostTripsByTripIdPublishApiResponse = /** status 200 Trip */ PublishResponse;
export type PostTripsByTripIdPublishApiArg = /** tripId */ string;
export type PostAuthApiResponse = /** status 200 session cookie */ SessionResponse;
export type PostAuthApiArg = {
  /** Allows userrs to stay logged in longer */
  rememberMe?: boolean;
  tokenBodyRequest: TokenBodyRequest;
};
export type DeleteAuthAccountApiResponse = /** status 200 undefined */ void;
export type DeleteAuthAccountApiArg = void;
export type PostAuthRefreshApiResponse = /** status 200 new session cookies */ GenericResponse;
export type PostAuthRefreshApiArg = void;
export type PostAuthLogoutApiResponse = /** status 200 Logout response */ LogoutResponse;
export type PostAuthLogoutApiArg = void;
export type GetAuthPermissionsApiResponse = /** status 200 Permissions */ PermissionsResponse;
export type GetAuthPermissionsApiArg = void;
export type GetAuthInspectApiResponse = /** status 200 Session */ InspectResponse;
export type GetAuthInspectApiArg = void;
export type GetNotificationsApiResponse = /** status 200 Notifications */ NotificationsResponse;
export type GetNotificationsApiArg = void;
export type PostNotificationsReadApiResponse = /** status 200 Profile */ GenericResponse;
export type PostNotificationsReadApiArg = NotificationsReadBodyRequest;
export type GetTripsByTripIdUrlApiResponse = /** status 200 Trip */ TripUrlResponse;
export type GetTripsByTripIdUrlApiArg = /** tripId */ string;
export type PostTripsByTripIdUrlAndLinkSecretApiResponse = /** status 200 Trip */ GenericResponse;
export type PostTripsByTripIdUrlAndLinkSecretApiArg = {
  /** tripId */
  tripId: string;
  /** linkSecret */
  linkSecret: string;
};
export type TripResponse = {
  id: string;
  title: string;
  startDate: string;
  endDate: string;
  postCount: number;
  ownerId: string;
  sortOrder: 'ASC' | 'DESC';
  headerFileName: string;
  shareMode: 'PUBLIC' | 'LINK' | 'PRIVATE';
  description: string;
  slug: string;
  linkSecret?: string;
  customCss?: string;
  unpublishedChanges: boolean;
  interactionsEnabled: boolean;
};
export type PartialTripBodyRequest = {
  title?: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  headerFileName?: string;
  shareMode?: 'PUBLIC' | 'LINK' | 'PRIVATE';
  slug?: string;
  customCss?: string;
  interactionsEnabled?: boolean;
  sortOrder?: 'ASC' | 'DESC';
};
export type DeleteTripResponse = {
  tripId: string;
};
export type TripsResponse = TripResponse[];
export type PostTripResponse = {
  tripId: string;
};
export type TripBodyRequest = {
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  headerFileName: string;
  shareMode: 'PUBLIC' | 'LINK' | 'PRIVATE';
  sortOrder: 'ASC' | 'DESC';
  slug?: string;
  customCss?: string;
  interactionsEnabled: boolean;
};
export type ToggleFavoriteResponse = {
  /** Whether the trip is now favorited (true) or unfavorited (false) */
  isFavorited: boolean;
};
export type FavoriteResponse = {
  trip: {
    id: string;
    title: string;
    description: string;
    startDate: string;
    endDate: string;
    headerFileName: string;
    postCount: number;
    ownerId: string;
  };
  lastKnownLinkSecret?: string;
};
export type FavoritesResponse = FavoriteResponse[];
export type StoriesResponse = {
  stories: {
    id: string;
    tripId: string;
    title: string;
    description: string;
    date: string;
    published: boolean;
    place: string;
    medias: {
      id: string;
      ext: string;
      dimensions: {
        width: number;
        height: number;
      };
    }[];
  }[];
};
export type StoryResponse = {
  id: string;
  tripId: string;
  title: string;
  description?: string;
  date: string;
  published?: boolean;
  place?: string;
  medias: {
    id: string;
    ext: string;
    dimensions: {
      width: number;
      height: number;
    };
  }[];
};
export type StoryBodyRequest = {
  title: string;
  description: string;
  date: string;
  place: string;
  medias: {
    id: string;
    ext: string;
    dimensions: {
      width: number;
      height: number;
    };
  }[];
};
export type PartialStoryBodyRequest = {
  title?: string;
  description?: string;
  date?: string;
  place?: string;
  medias?: {
    id: string;
    ext: string;
    dimensions: {
      width: number;
      height: number;
    };
  }[];
};
export type DeleteResponse = {
  /** id of deleted story */
  id: string;
};
export type OwnProfileResponse = {
  userId: string;
  username: string;
  description: string;
  email: string;
  creationYear: string;
};
export type ProfileResponse = {
  userId: string;
  username: string;
  description: string;
  email?: string;
  creationYear: string;
};
export type ProfileBodyRequest = {
  username?: string;
  description?: string;
};
export type InteractionsResponse = {
  stories: {
    storyId: string;
    likes: {
      userId: string;
    }[];
    comments: {
      id: string;
      name?: string;
      userId?: string;
      body: string;
      /** creation time in ISO format */
      createdAt: string;
    }[];
  }[];
};
export type AllInteractionsResponse = {
  trips: {
    tripId: string;
    tripTitle: string;
    interactions: {
      stories: {
        storyId: string;
        likes: {
          userId: string;
        }[];
        comments: {
          id: string;
          body: string;
          createdAt: string;
          name?: string | null;
          userId?: string | null;
        }[];
      }[];
    };
    views: number;
  }[];
};
export type CommentBodyRequest = {
  storyId: string;
  name?: string;
  userId?: string;
  body: string;
};
export type LikeBodyRequest = {
  storyId: string;
};
export type ViewsResponse = {
  viewCount?: number;
};
export type MediaUploadResponse = {
  uploadUrls: {
    filename: string;
    uploadUrl: string;
    uploadId: string;
  }[];
};
export type MediaUploadRequest = {
  storyId?: string;
  filenames: {
    filename: string;
  }[];
};
export type ProfilePicUploadRequest = {
  filename: string;
};
export type PublishResponse = {
  tripId: string;
  success: boolean;
};
export type SessionResponse = {
  sessionId: string;
};
export type TokenBodyRequest = {
  idToken: string;
};
export type GenericResponse = {
  success: boolean;
};
export type LogoutResponse = {
  success: boolean;
};
export type PermissionsResponse = {
  globalPermissions: string[];
  permissions: {
    tripId: string;
    role: string;
    userId: string;
  }[];
};
export type InspectResponse = {
  active?: {
    /** The user id */
    userId: string;
    /** The timestamp of the session expiry in seconds */
    sessionExpiresAt: number;
    /** Whether the user has a remember me session */
    rememberMe: boolean;
    /** The timestamp of the remember me expiry in seconds */
    rememberMeExpiresAt?: number;
  };
};
export type NotificationsResponse = {
  id: string;
  message: string;
  creation: string;
  read: boolean;
  tripId: string;
  storyId: string;
}[];
export type NotificationsReadBodyRequest = {
  notificationIds: string[];
};
export type TripUrlResponse = {
  url?: string;
};
export const {
  useGetTripsByTripIdQuery,
  useLazyGetTripsByTripIdQuery,
  usePatchTripsByTripIdMutation,
  useDeleteTripsByTripIdMutation,
  useGetTripsQuery,
  useLazyGetTripsQuery,
  usePostTripsMutation,
  useGetTripsByUserByUserIdQuery,
  useLazyGetTripsByUserByUserIdQuery,
  usePostTripsByTripIdFavoriteAndLinkSecretMutation,
  useGetTripsFavoritesQuery,
  useLazyGetTripsFavoritesQuery,
  useGetStoriesByTripByTripIdQuery,
  useLazyGetStoriesByTripByTripIdQuery,
  usePostStoriesByTripByTripIdMutation,
  usePatchStoriesByTripByTripIdStoryAndStoryIdMutation,
  useDeleteStoriesByTripByTripIdStoryAndStoryIdMutation,
  useGetProfilesQuery,
  useLazyGetProfilesQuery,
  usePutProfilesMutation,
  useGetProfilesByUserIdQuery,
  useLazyGetProfilesByUserIdQuery,
  useGetInteractionsByTripByTripIdAndLinkSecretQuery,
  useLazyGetInteractionsByTripByTripIdAndLinkSecretQuery,
  useGetInteractionsByUserQuery,
  useLazyGetInteractionsByUserQuery,
  usePostInteractionsByTripByTripIdAndLinkSecretCommentMutation,
  usePostInteractionsByTripByTripIdAndLinkSecretLikeMutation,
  usePostInteractionsByTripByTripIdAndLinkSecretViewsMutation,
  useGetInteractionsByTripByTripIdViewsQuery,
  useLazyGetInteractionsByTripByTripIdViewsQuery,
  useDeleteInteractionsByTripByTripIdCommentAndCommentIdMutation,
  usePostUploadsByTripByTripIdGenerateUrlsMutation,
  usePostUploadsProfilePictureMutation,
  usePostTripsByTripIdPublishMutation,
  usePostAuthMutation,
  useDeleteAuthAccountMutation,
  usePostAuthRefreshMutation,
  usePostAuthLogoutMutation,
  useGetAuthPermissionsQuery,
  useLazyGetAuthPermissionsQuery,
  useGetAuthInspectQuery,
  useLazyGetAuthInspectQuery,
  useGetNotificationsQuery,
  useLazyGetNotificationsQuery,
  usePostNotificationsReadMutation,
  useGetTripsByTripIdUrlQuery,
  useLazyGetTripsByTripIdUrlQuery,
  usePostTripsByTripIdUrlAndLinkSecretMutation,
} = injectedRtkApi;
